import React from 'react'
import { Modal, Highlight, ContextNotification } from '@pandiafi/components'
import { useTranslation } from 'react-i18next'
import { Button } from '@pandiafi/design-system'

import type {
  CoreParkingArea,
  CoreParkingSpot,
} from '../../../common/validations/contractParkingSpotsSchema'
import { todayISO, mapStatus } from './parkingSpots.helpers'
import {
  spotEquipmentLabel,
  findActiveReservation,
  findFutureReservation,
  spotDisplayId,
  formatDate,
  isDisabledSpot,
} from './uiSelectors'
import { ParkingSpotReservationForm } from './ParkingSpotReservationForm'
import { ParkingSpotSuccess } from './ParkingSpotSuccess'
import { ParkingSpotDetails } from './ParkingSpotDetails'
import { ParkingSpotTerminateForm } from './ParkingSpotTerminateForm'
import { ParkingSpotCancellationForm } from './ParkingSpotCancellationForm'

export interface ParkingSpotModalProps {
  spot: CoreParkingSpot | null
  area: CoreParkingArea | null
  onClose: () => void
  reservationPossibleUntil: string | null
  hasReachedLimit?: boolean
  limit?: number | null
  canReserveDisabledSpots?: boolean
  complimentarySlotsRemaining?: number
  reservationsEnabled?: boolean
}

const findTerminableReservation = (spot: CoreParkingSpot) => {
  const active = findActiveReservation(spot)
  if (active && !active.endDate && active.kind === 'own') {
    return active
  }

  return null
}

const findCancellableReservation = (spot: CoreParkingSpot) => {
  const future = findFutureReservation(spot)
  if (future && !future.endDate && future.kind === 'own') {
    return future
  }
  return null
}

export const ParkingSpotModal: React.FC<ParkingSpotModalProps> = ({
  spot,
  area,
  onClose,
  reservationPossibleUntil = null,
  hasReachedLimit = false,
  limit = null,
  canReserveDisabledSpots = true,
  complimentarySlotsRemaining = 0,
  reservationsEnabled = true,
}) => {
  const { t, i18n } = useTranslation(['parking'])
  const { t: tCommon } = useTranslation('common')
  const lang = i18n.language

  const computedStatus = spot ? mapStatus(spot) : undefined
  const [newReservationSuccessInfo, setNewReservationSuccessInfo] =
    React.useState<{
      fromISO: string
      isComplimentary: boolean
    } | null>(null)

  React.useEffect(() => {
    setNewReservationSuccessInfo(null)
  }, [spot?.rentableParkingSpotId])

  if (
    !spot ||
    !area ||
    computedStatus === undefined ||
    ['disabled', 'not-in-use'].includes(computedStatus)
  ) {
    return null
  }

  const equipmentLabel = spotEquipmentLabel(spot, t)
  const terminableReservation = findTerminableReservation(spot)
  const cancellableReservation = findCancellableReservation(spot)

  const disabledSpotReservationBlocked =
    isDisabledSpot(spot) && !canReserveDisabledSpots
  const reservationLimitReached = hasReachedLimit
  const reservationBlocked =
    disabledSpotReservationBlocked || reservationLimitReached

  const scheduledEndISO = (spot && findActiveReservation(spot)?.endDate) ?? null
  const hasScheduledEnd = !!scheduledEndISO

  const shouldShowTerminateInfo = !newReservationSuccessInfo && hasScheduledEnd
  const shouldShowTerminateForm =
    !newReservationSuccessInfo && !hasScheduledEnd && terminableReservation
  const shouldShowCancelForm =
    !newReservationSuccessInfo && !hasScheduledEnd && cancellableReservation
  const shouldShowCreateForm =
    !newReservationSuccessInfo &&
    !(terminableReservation || cancellableReservation) &&
    computedStatus === 'default' &&
    !hasScheduledEnd &&
    !reservationBlocked &&
    reservationsEnabled
  const shouldShowDetails =
    !newReservationSuccessInfo &&
    !(terminableReservation || cancellableReservation) &&
    !hasScheduledEnd &&
    computedStatus !== 'default'
  const shouldShowBlockedInfo =
    !newReservationSuccessInfo &&
    !(terminableReservation || cancellableReservation) &&
    computedStatus === 'default' &&
    reservationBlocked

  const displayId = spotDisplayId(spot)
  const headerTitle =
    computedStatus === 'default' ? t('view.reserve') : t('view.details')

  return (
    <Modal
      show
      onClose={onClose}
      id={`parking-spot-${spot.rentableParkingSpotId}`}
      title={headerTitle}
      width="m"
      headerElement={<Highlight>{t('view.parkingSpot')}</Highlight>}
    >
      <div className="space-y-5">
        {newReservationSuccessInfo && (
          <>
            <Modal.Notifications>
              <ParkingSpotSuccess
                spot={spot}
                area={area}
                fromISO={newReservationSuccessInfo.fromISO}
                isComplimentary={newReservationSuccessInfo.isComplimentary}
              />
            </Modal.Notifications>
            <Modal.Buttons>
              <Button variant="cancel" onClick={onClose}>
                {tCommon('closeButton')}
              </Button>
            </Modal.Buttons>
          </>
        )}

        {shouldShowTerminateInfo && (
          <>
            <Modal.Notifications>
              <ContextNotification type="success">
                <div className="font-semibold">
                  {t('terminate.terminateTitle')}
                </div>
                <div>
                  {t('terminate.terminateFrom', {
                    spot: displayId,
                    date: formatDate(scheduledEndISO, lang),
                  })}
                </div>
              </ContextNotification>
            </Modal.Notifications>
            <Modal.Buttons>
              <Button variant="cancel" onClick={onClose}>
                {tCommon('closeButton')}
              </Button>
            </Modal.Buttons>
          </>
        )}
        {shouldShowTerminateForm && (
          <ParkingSpotTerminateForm
            key={spot.rentableParkingSpotId}
            rentableParkingSpotId={spot.rentableParkingSpotId}
            reservation={terminableReservation}
            onClose={onClose}
          />
        )}
        {shouldShowCancelForm && (
          <ParkingSpotCancellationForm
            key={spot.rentableParkingSpotId}
            rentableParkingSpotId={spot.rentableParkingSpotId}
            reservation={cancellableReservation}
            onClose={onClose}
          />
        )}
        {shouldShowBlockedInfo && (
          <>
            <Modal.Notifications>
              {disabledSpotReservationBlocked && (
                <ContextNotification type="warning">
                  {t('view.disabledReservationInfo')}
                </ContextNotification>
              )}
              {reservationLimitReached && (
                <ContextNotification type="warning">
                  {t('view.limitReached', { max: limit ?? 0 })}
                </ContextNotification>
              )}
            </Modal.Notifications>
            <Modal.Buttons>
              <Button variant="cancel" onClick={onClose}>
                {tCommon('closeButton')}
              </Button>
            </Modal.Buttons>
          </>
        )}
        {shouldShowCreateForm && (
          <ParkingSpotReservationForm
            spot={spot}
            area={area}
            reservationPossibleUntil={reservationPossibleUntil}
            startDate={todayISO()}
            isComplimentary={complimentarySlotsRemaining > 0}
            onSuccess={(fromISO, isComplimentary) =>
              setNewReservationSuccessInfo({ fromISO, isComplimentary })
            }
            onClose={onClose}
          />
        )}
        {shouldShowDetails && (
          <ParkingSpotDetails
            spot={spot}
            status={computedStatus}
            equipmentTextOverride={equipmentLabel}
          />
        )}
      </div>
    </Modal>
  )
}
